import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState, useAppDispatch } from "../../redux/store";
import { parseScheduleExcel } from "../../utils/parseScheduleExcel";
import { matchScheduleEmployees } from "../../utils/matchScheduleEmployees";
import { isManagerOrOwner } from "../../utils/user";
import {
  setParsedData,
  setError,
  resetUpload,
  setEmployeeMappings,
  setFetchingEmployees,
} from "../../redux/features/scheduleUpload/scheduleUploadSlice";
import { getEmployees } from "../../redux/features/common/api";
import {
  UploadDropZone,
  UploadSummaryCard,
  UnmatchedEmployeeWarning,
} from "../../components/ScheduleUpload";

// ─── Manager Guard ────────────────────────────────────────────────────────────

const AccessDenied: React.FC = () => (
  <div className="flex min-h-[80vh] items-center justify-center p-6">
    <div className="w-full max-w-md rounded-2xl border border-red-200 bg-gradient-to-br from-red-50 to-white p-10 text-center shadow-sm">
      <svg className="mx-auto mb-4 h-14 w-14 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0-8v4m0-4a4 4 0 00-4 4v1H5a2 2 0 00-2 2v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 00-2-2h-3v-1a4 4 0 00-4-4z" />
      </svg>
      <h2 className="text-xl font-extrabold text-red-700">Manager Access Required</h2>
      <p className="mt-2 text-sm text-gray-500">
        The Schedule Upload portal is only accessible to managers. Please contact your administrator if you believe this is incorrect.
      </p>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ScheduleUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const uploadState = useSelector((state: RootState) => state.scheduleUpload);

  const [localError, setLocalError] = React.useState<string | null>(null);
  const [isParsingFile, setIsParsingFile] = React.useState(false);

  // ─── Auth guard ──────────────────────────────────────────────────────────────
  const user = useSelector((state: RootState) => state.user.user);
  const isManager = isManagerOrOwner(user);
  if (!isManager) return <AccessDenied />;

  // ─── File handler ─────────────────────────────────────────────────────────────
  const handleFileSelect = async (file: File) => {
    setLocalError(null);
    if (!file.name.match(/\.(xlsx|xls|ods)$/i)) {
      setLocalError("Please upload a valid file (.xlsx, .xls, or .ods).");
      return;
    }

    setIsParsingFile(true);
    dispatch(resetUpload());

    try {
      const parsed = await parseScheduleExcel(file);
      dispatch(setParsedData(parsed));

      // Fetch all employees and map names → IDs
      dispatch(setFetchingEmployees(true));
      const result = await dispatch(
        getEmployees({ l_i_m_i_t: "500", module_name: "schedule_upload" })
      );

      console.debug("[ScheduleUpload] getEmployees result:", result);

      if (getEmployees.fulfilled.match(result)) {
        const apiEmployees = result.payload.data ?? [];
        console.debug("[ScheduleUpload] API employees count:", apiEmployees.length);
        console.debug("[ScheduleUpload] Sample API employees:", apiEmployees.slice(0, 3).map(e => ({
          employee_id: e.employee_id,
          full_name: e.full_name,
          first_name: e.first_name,
          last_name: e.last_name,
        })));
        console.debug("[ScheduleUpload] Excel employee names:", parsed.rows.map(r => r.employee));

        const mappings = matchScheduleEmployees(
          parsed.rows.map((r) => r.employee),
          apiEmployees
        );
        console.debug("[ScheduleUpload] Name → ID mappings:", mappings);
        dispatch(setEmployeeMappings({ mappings }));
      } else {
        console.error("[ScheduleUpload] getEmployees FAILED:", result);
      }
      dispatch(setFetchingEmployees(false));
    } catch (err: any) {
      const msg = err.message || "Failed to parse Excel file.";
      dispatch(setError(msg));
      setLocalError(msg);
      toast.error(msg, { autoClose: 8000 });
      dispatch(setFetchingEmployees(false));
    } finally {
      setIsParsingFile(false);
    }
  };

  const hasParsed = uploadState.uploadStatus === "parsed" && !!uploadState.parsedData;
  const isLoading = isParsingFile || uploadState.isFetchingEmployees;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 md:px-8">
      {/* ── Header ── */}
      <div className="mb-2 flex items-center gap-3">
        <svg className="h-9 w-9 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
        </svg>
        <div>
          <h1 className="text-3xl font-extrabold leading-tight text-gray-800">Schedule Upload</h1>
          <p className="text-sm text-gray-500">Upload an Excel schedule for review and approval</p>
        </div>
      </div>

      {/* ── Stepper ── */}
      <StepIndicator active={0} />

      {/* ── Drop Zone ── */}
      <UploadDropZone
        onFileSelect={handleFileSelect}
        isLoading={isLoading}
        hasParsed={hasParsed}
        fileName={uploadState.parsedData?.fileName}
      />

      {/* ── Error ── */}
      {(localError || uploadState.errorMessage) && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {localError || uploadState.errorMessage}
        </div>
      )}

      {/* ── Unmatched employee warning ── */}
      {hasParsed && (
        <UnmatchedEmployeeWarning names={uploadState.unmatchedEmployees} />
      )}

      {/* ── Parsed Summary ── */}
      {hasParsed && uploadState.parsedData && (
        <UploadSummaryCard
          fileName={uploadState.parsedData.fileName}
          rows={uploadState.parsedData.rows}
          weeks={uploadState.parsedData.rows[0]?.weeks ?? []}
          isFetchingEmployees={uploadState.isFetchingEmployees}
          onReset={() => {
            dispatch(resetUpload());
            setLocalError(null);
          }}
          onProceed={() => navigate("/schedule-upload/review")}
        />
      )}
    </div>
  );
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

export const StepIndicator: React.FC<{ active: number }> = ({ active }) => {
  const steps = ["Upload", "Review", "Approval", "Saved"];
  return (
    <div className="mt-6 mb-2 flex items-center">
      {steps.map((step, i) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center" style={{ minWidth: 70 }}>
            <div
              className={`mb-1 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                i < active
                  ? "bg-gradient-to-br from-green-400 to-green-700 text-white"
                  : i === active
                  ? "bg-gradient-to-br from-blue-500 to-blue-700 text-white"
                  : "bg-gray-200 text-gray-400"
              }`}
            >
              {i < active ? "✓" : i + 1}
            </div>
            <span
              className={`text-xs ${
                i === active
                  ? "font-bold text-blue-600"
                  : i < active
                  ? "font-medium text-green-600"
                  : "text-gray-400"
              }`}
            >
              {step}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`mb-5 h-0.5 flex-1 transition-all duration-300 ${
                i < active ? "bg-green-400" : "bg-gray-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ScheduleUploadPage;
